import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { authHeaders } from "@/services/auth";
import { version as reactVersion } from "react";
import { localeHeaders, translate, useT } from "@/i18n";

// `state` is in the reader's language; `code` is the same state as a fixed word, for the colour.
type Check = { name: string; state: string; detail: string; code?: string };
type StatusResponse = { checks: Check[]; generated_at: string };

async function fetchStatus(): Promise<StatusResponse> {
  const res = await fetch("/api/v1/status", { headers: { Accept: "application/json", ...localeHeaders(), ...authHeaders() } });
  if (!res.ok) throw new Error(translate("error.request", { status: res.status }));
  return res.json();
}

/**
 * The skeleton's proof of chain. It is deliberately boring, and it is deliberately end to end:
 * the browser got a bundle, React mounted, the bundle asked Rails, Rails asked Postgres. Each row
 * below is a link in that chain that is now known to work, rather than assumed to.
 *
 * Replace this page the moment there is a real first screen.
 */
export default function StatusPage() {
  const { staff, signOut } = useAuth();
  const { t } = useT();
  const { data, error, isPending } = useQuery({ queryKey: ["status"], queryFn: fetchStatus });

  const frontEndChecks: Check[] = [
    { name: t("status.browser"), state: "ok", detail: t("status.mounted") },
    { name: "React", state: "ok", detail: reactVersion },
  ];

  return (
    <main className="min-h-screen px-4 py-12 sm:py-20">
      <div className="mx-auto w-full max-w-2xl">
        <header className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t("login.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {t("status.intro")}
          </p>
          <p className="mt-3 text-sm text-slate-500">
            {t("status.signedInAs")} <span className="font-medium text-slate-700">{staff?.name || staff?.email}</span>
            {" · "}
            <button onClick={() => void signOut()} className="underline underline-offset-2 hover:text-slate-900">
              {t("nav.signOut")}
            </button>
          </p>
        </header>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <Section title={t("status.frontEnd")} checks={frontEndChecks} />

          {isPending && <Row name={t("status.server")} state="…" detail={t("status.asking")} />}

          {error && (
            <Row
              name={t("status.server")}
              state={t("status.unreachable")}
              code="unreachable"
              detail={error instanceof Error ? error.message : String(error)}
            />
          )}

          {data && <Section title={t("status.server")} checks={data.checks} />}
        </div>

        {data && (
          <p className="mt-4 text-xs text-slate-400">
            {t("status.answeredAt", { when: new Date(data.generated_at).toLocaleString() })}
          </p>
        )}
      </div>
    </main>
  );
}

function Section({ title, checks }: { title: string; checks: Check[] }) {
  return (
    <>
      <h2 className="border-b border-slate-200 bg-slate-50 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </h2>
      {checks.map((c) => (
        <Row key={c.name} {...c} />
      ))}
    </>
  );
}

function Row({ name, state, detail, code }: Check) {
  const bad = code === "unreachable";
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-slate-100 px-5 py-3 last:border-b-0">
      <span className="w-28 shrink-0 text-sm font-medium text-slate-700">{name}</span>
      <span
        className={`rounded px-2 py-0.5 text-xs font-semibold ${
          bad ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
        }`}
      >
        {state}
      </span>
      <span className="min-w-0 flex-1 break-words text-sm text-slate-500">{detail}</span>
    </div>
  );
}
